import React, { useState, useEffect } from 'react';
import DrillCard from './DrillCard';
import DrillDetail from './DrillDetail';

function DrillLibrary() {
  const [drills, setDrills] = useState([]);
  const [ages, setAges] = useState(["Tutte"]);
  const [categories, setCategories] = useState(["Tutte"]);
  const [duration, setDuration] = useState("");
  const [selected, setSelected] = useState(null);
  const [drillsPath, setDrillsPath] = useState("");
  const [objectives, setObjectives] = useState([]);
  const [selectedObjectives, setSelectedObjectives] = useState([]);

  const ageOptions = ["U10", "U12", "U14", "U15"];
  const [allDrills, setAllDrills] = useState([]); // dataset filtered by category only
  const [availableAges, setAvailableAges] = useState(ageOptions);

  const computeAvailableAges = (list) => {
    const set = new Set();
    list.forEach(d => {
      const ages = Array.isArray(d.age_categories) ? d.age_categories : (d.age_category ? [d.age_category] : []);
      if (ages.includes("Tutte")) {
        ageOptions.forEach(a => set.add(a));
      } else {
        ages.forEach(a => set.add(a));
      }
    });
    return ageOptions.filter(a => set.has(a));
  };

  const API_BASE = process.env.REACT_APP_API || "http://localhost:8000";

  useEffect(() => {
    // Fetch objectives on mount
    fetch(`${API_BASE}/objectives`)
      .then(r => r.json())
      .then(data => setObjectives(data.objectives || []))
      .catch(() => setObjectives([]));
  }, []);

  useEffect(() => {
    // Build category-only query
    let query = "";
    if (!categories.includes("Tutte")) {
      query = `?category=${categories.join(",")}`;
    }
    fetch(`${API_BASE}/drills${query}`)
      .then(r => r.json())
      .then(list => {
        // Fallback client-side filter by category if backend doesn't support it
        let filtered = list;
        if (!categories.includes("Tutte")) {
          filtered = list.filter(d => {
            const cs = Array.isArray(d.categories) ? d.categories : [];
            return cs.includes("Tutte") || cs.some(c => categories.includes(c));
          });
        }
        setAllDrills(filtered);
        const avail = computeAvailableAges(filtered);
        setAvailableAges(avail);
        // Clamp current age selection to available; if none remain, reset to ["Tutte"]
        if (ages.includes("Tutte")) return; // keep as all
        const nextAges = ages.filter(a => avail.includes(a));
        setAges(nextAges.length ? nextAges : ["Tutte"]);
      })
      .then(() => {
        fetch(`${API_BASE}/config`)
          .then(r => r.json())
          .then(data => setDrillsPath(data.drills_path || ""));
      });
  }, [categories]);

  useEffect(() => {
    let list = allDrills;
    // Filter by ages (respecting "Tutte")
    if (!ages.includes("Tutte")) {
      list = list.filter(d => {
        const as = Array.isArray(d.age_categories) ? d.age_categories : (d.age_category ? [d.age_category] : []);
        if (as.includes("Tutte")) return true; // appears in all
        return as.some(a => ages.includes(a));
      });
    }
    // Filter by duration (minimum minutes)
    if (duration) {
      const min = Number(duration);
      list = list.filter(d => Number(d.duration_min || 0) >= min);
    }
    // Filter by selected objectives
    if (selectedObjectives.length > 0) {
      list = list.filter(d => {
        const primary = Array.isArray(d.primary_objectives) ? d.primary_objectives : [];
        const secondary = Array.isArray(d.secondary_objectives) ? d.secondary_objectives : [];
        return selectedObjectives.some(obj => primary.includes(obj) || secondary.includes(obj));
      });
    }
    setDrills(list);
  }, [allDrills, ages, duration, selectedObjectives]);

  const refreshByCategory = () => {
    let query = "";
    if (!categories.includes("Tutte")) {
      query = `?category=${categories.join(",")}`;
    }
    return fetch(`${API_BASE}/drills${query}`)
      .then(r => r.json())
      .then(list => {
        let filtered = list;
        if (!categories.includes("Tutte")) {
          filtered = list.filter(d => {
            const cs = Array.isArray(d.categories) ? d.categories : [];
            return cs.includes("Tutte") || cs.some(c => categories.includes(c));
          });
        }
        setAllDrills(filtered);
        const avail = computeAvailableAges(filtered);
        setAvailableAges(avail);
      });
  };

  const saveDrill = (updated) => {
    fetch(`${API_BASE}/drills`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updated)
    })
      .then(() => refreshByCategory())
      .then(() => setSelected(null))
      .catch(err => console.error("Save error", err));
  };

  if (selected) {
    return (
      <DrillDetail
        drill={selected}
        onBack={() => setSelected(null)}
        onSaveOverwrite={(drill) => saveDrill({ ...drill, overwrite: true })}
        onSaveNew={(drill) => saveDrill({ ...drill, overwrite: false })}
      />
    );
  }

  return (
    <div>
      <div className="mb-3">
        <label className="form-label"><strong>Categorie</strong></label>
        <div>
          {["Tecnica", "Tattica", "Preparazione Atletica", "Tecnica del portiere", "Tutte"].map((cat, idx) => {
            const active = categories.includes(cat);
            return (
              <span
                key={idx}
                onClick={() => {
                  if (cat === "Tutte") {
                    setCategories(["Tutte"]);
                  } else {
                    if (categories.includes("Tutte")) {
                      setCategories([cat]);
                    } else if (active) {
                      setCategories(categories.filter(c => c !== cat));
                    } else {
                      setCategories([...categories, cat]);
                    }
                  }
                }}
                style={{
                  display: "inline-block",
                  margin: "4px",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  backgroundColor: active ? "#17a2b8" : "#e0e0e0",
                  color: active ? "white" : "black",
                  cursor: "pointer"
                }}
              >
                {cat}
              </span>
            );
          })}
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label"><strong>Obiettivi</strong></label>
        <div>
          {objectives.map((obj, idx) => {
            const active = selectedObjectives.includes(obj);
            return (
              <span
                key={idx}
                onClick={() => {
                  if (active) {
                    setSelectedObjectives(selectedObjectives.filter(o => o !== obj));
                  } else {
                    setSelectedObjectives([...selectedObjectives, obj]);
                  }
                }}
                style={{
                  display: "inline-block",
                  margin: "4px",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  backgroundColor: active ? "#28a745" : "#e0e0e0",
                  color: active ? "white" : "black",
                  cursor: "pointer"
                }}
              >
                {obj}
              </span>
            );
          })}
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label"><strong>Categorie Età</strong></label>
        <div>
          {["U10", "U12", "U14", "U15", "Tutte"].map((cat, idx) => {
            const active = ages.includes(cat);
            const isDisabled = cat !== "Tutte" && !availableAges.includes(cat);
            return (
              <span
                key={idx}
                onClick={() => {
                  if (isDisabled) return;
                  if (cat === "Tutte") {
                    setAges(["Tutte"]);
                  } else {
                    if (ages.includes("Tutte")) {
                      setAges([cat]);
                    } else if (active) {
                      setAges(ages.filter(c => c !== cat));
                    } else {
                      setAges([...ages, cat]);
                    }
                  }
                }}
                style={{
                  display: "inline-block",
                  margin: "4px",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  backgroundColor: active ? "#007bff" : isDisabled ? "#f0f0f0" : "#e0e0e0",
                  color: active ? "white" : isDisabled ? "#aaa" : "black",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.6 : 1
                }}
              >
                {cat}
              </span>
            );
          })}
        </div>
      </div>
      <div className="mt-3">
        <label className="form-label">
          Durata esercizio (minuti): {duration || 0}
        </label>
        <input
          type="range"
          className="form-range"
          min="0"
          max="120"
          step="5"
          value={duration}
          onChange={e => setDuration(e.target.value)}
        />
      </div>
      <div className="mb-3">
        <small style={{color: "#666"}}>
          Path drills.json: {drillsPath 
            ? (drillsPath.startsWith("gs://") 
                ? `${drillsPath}/drills.json` 
                : `${window.location.origin}/${drillsPath}/drills.json`)
            : ""}
        </small>
      </div>
      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-3">
        {drills.map(d => (
          <div
            key={d.id}
            className="col"
            onClick={() => setSelected(d)}
            style={{
              transition: "transform 0.2s, box-shadow 0.2s",
              cursor: "pointer",
              minWidth: "420px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "scale(1.03)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
            }}
          >
            <DrillCard drill={d} />
          </div>
        ))}
        <div
          className="col d-flex align-items-center justify-content-center"
          style={{
            minWidth: "420px",
            border: "2px dashed #ccc",
            borderRadius: "8px",
            background: "transparent",
            cursor: "pointer",
            transition: "transform 0.2s"
          }}
          onClick={() => setSelected({
            id: null,
            title: "Nuovo esercizio",
            age_categories: ages.includes("Tutte") ? ["Tutte"] : ages,
            categories: categories.includes("Tutte") ? ["Tutte"] : categories,
            complexity: "",
            execution: "",
            primary_objectives: [],
            secondary_objectives: [],
            format: "",
            duration_min: "",
            constraints: "",
            equipment: "",
            positions: [],
            balls: [],
            miniGoals: [],
            cones: [],
            markers: []
          })}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        >
          <span style={{ fontSize: "1.2rem", color: "#777" }}>➕ Nuovo esercizio</span>
        </div>

        
      </div>
    </div>
  );
}

export default DrillLibrary;